import { useEffect, useState } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import STLogo from "@/components/STLogo";
import { useI18n } from "@/i18n/I18nProvider";

export default function Header() {
  const { t, toggleLocale, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28, mass: 0.4 });

  const links = [
    { key: "about", href: "/#about" },
    { key: "services", href: "/#services" },
    { key: "products", href: "/products" },
    { key: "roadmap", href: "/#roadmap" },
    { key: "process", href: "/#process" },
    { key: "leadership", href: "/#leadership" },
    { key: "contact", href: "/#contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-header"
      initial={{ y: -64, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-white/85 border-b border-line shadow-[0_1px_24px_rgba(9,9,11,0.05)]"
          : "bg-white/0 border-b border-transparent"
      }`}
    >
      {/* scroll progress */}
      <motion.div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-[2px] origin-left bg-brand-red"
        style={{ scaleX: progress }}
      />
      <div className="container-pro flex items-center justify-between py-4">
        <div className="shrink-0" data-testid="header-logo-link">
          <STLogo />
        </div>

        <nav className="hidden md:flex items-center gap-8" data-testid="primary-nav">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.key}`}
              className="group relative text-sm text-ink-secondary hover:text-ink-primary transition-colors"
            >
              {t.nav[l.key]}
              <span className="absolute -bottom-1 start-0 h-px w-0 bg-brand-red transition-all duration-300 group-hover:w-full" aria-hidden />
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button
            type="button"
            onClick={toggleLocale}
            data-testid="lang-toggle"
            aria-label="Toggle language"
            className="inline-flex items-center gap-2 px-3 py-2 text-xs font-mono uppercase tracking-widest text-ink-secondary hover:text-ink-primary border border-line rounded-full hover:border-brand-red transition-colors"
          >
            <Globe size={14} /> {t.nav.switchLang}
          </button>
          <motion.a
            href="/#contact"
            className="btn-primary"
            data-testid="header-cta"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            {t.nav.bookConsultation}
          </motion.a>
        </div>

        <div className="md:hidden flex items-center gap-2">
          <button
            type="button"
            onClick={toggleLocale}
            data-testid="lang-toggle-mobile"
            aria-label="Toggle language"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-widest text-ink-secondary border border-line rounded-full"
          >
            <Globe size={12} /> {locale === "en" ? "AR" : "EN"}
          </button>
          <button
            className="p-2 text-ink-primary"
            aria-label="Open menu"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="md:hidden border-t border-line bg-white/95 backdrop-blur-xl"
          data-testid="mobile-menu"
        >
          <div className="container-pro py-4 flex flex-col gap-3">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-sm py-2 text-ink-secondary hover:text-ink-primary"
                data-testid={`mobile-nav-link-${l.key}`}
              >
                {t.nav[l.key]}
              </a>
            ))}
            <a href="/#contact" onClick={() => setOpen(false)} className="btn-primary mt-2" data-testid="mobile-header-cta">
              {t.nav.bookConsultation}
            </a>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
